import React, {ComponentProps, forwardRef, useEffect, useState} from 'react';
import {IMask, useIMask} from 'react-imask';
import moment from 'moment';
import InputText from './InputText';

type InputDateProps = ComponentProps<typeof InputText> & {
    format?: string;
    maxDate?: Date;
    minDate?: Date;
};

const getBlocks = () => ({
    YY: {
        mask: IMask.MaskedRange,
        from: 0,
        to: 99,
    },
    YYYY: {
        mask: IMask.MaskedRange,
        from: 1900,
        to: 9999,
    },
    MM: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 12,
    },
    DD: {
        mask: IMask.MaskedRange,
        from: 1,
        to: 31,
    },
});

const InputDate = forwardRef<HTMLInputElement, InputDateProps>(
    ({value, format = 'MM/DD/YYYY', minDate, maxDate, ...props}, ref) => {
        const formatter = (date: Date) => moment(date).format(format);
        const parser = (str: string) => moment(str, format);

        const {ref: inputMaskRef, setValue} = useIMask({
            mask: Date,
            pattern: format,
            format: formatter,
            parse: parser,
            blocks: getBlocks(),
            overwrite: false,
            autofix: 'pad',
            max: maxDate || new Date(new Date().setFullYear(new Date().getFullYear() + 100)),
            min: minDate,
        });

        const initialRender = React.useRef(true);

        useEffect(() => {
            if (value && initialRender.current) {
                const isValidFormat = moment(value, format, true).isValid();

                if (isValidFormat) {
                    setValue(value);
                    initialRender.current = false;
                }
            }
        }, [value, setValue]);

        const handleRefs = (instance: HTMLInputElement | null) => {
            if (ref) {
                if (typeof ref === 'function') ref(instance);
                else ref.current = instance;
            }

            if (instance) inputMaskRef.current = instance;
        };

        return <InputText ref={handleRefs} {...props} placeholder={format} InputLabelProps={{shrink: true}} />;
    },
);

InputDate.displayName = 'InputDate';
export default InputDate;
